package com.dwarshb.bms

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AcUnit
import androidx.compose.material.icons.filled.AirportShuttle
import androidx.compose.material.icons.filled.DirectionsBus
import androidx.compose.material.icons.filled.DirectionsBusFilled
import androidx.compose.material.icons.filled.EventSeat
import androidx.compose.material.icons.filled.HelpOutline
import androidx.compose.material.icons.filled.Hotel
import androidx.compose.material.icons.filled.WbSunny
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Divider
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.dwarshb.firebase.FirebaseDatabase
import com.dwarshb.firebase.onCompletion
import kotlinx.coroutines.launch

// Data class remains the same
data class RadioOption(
    val id: String,
    val title: String
)

@Composable
fun BusBookingFormCreative(onBackPressed: ()->Unit) { // Renamed for clarity
    // State variables remain the same
    var name by remember { mutableStateOf("") }
    var sourceLocation by remember { mutableStateOf("") }
    var destinationLocation by remember { mutableStateOf("") }
    var date by remember { mutableStateOf("") }
    var days by remember { mutableStateOf("") }
    var selectedBusSize by remember { mutableStateOf<String?>(null) }
    var selectedBusType by remember { mutableStateOf<String?>(null) }
    var selectedBusCategory by remember { mutableStateOf<String?>(null) }
    var budget by remember { mutableStateOf("") }
    var phoneNumber by remember { mutableStateOf("") }

    var openDialog by remember { mutableStateOf(false) }
    var openDialogTitle by remember { mutableStateOf("") }
    var openDialogText by remember { mutableStateOf("") }

    var firebaseDatabase = FirebaseDatabase()
    var coroutineScope = rememberCoroutineScope()
    var URIHandler = LocalUriHandler.current

    // Options remain the same
    val busSizeOptions = listOf(
        RadioOption(id = "0_minubus", title = "Mini Bus \n (16 to 21 passengers)"),
        RadioOption(id = "1_mini_coach", title = "Mini Coach Bus \n (20 to 30 passengers)"),
        RadioOption(id = "2_coach_bus", title = "Coach Bus \n (49 to 59 passengers)")
    )

    val busTypeOptions = listOf(
        RadioOption(id = "0_ac", title = "AC Bus"),
        RadioOption(id = "1_nonac", title = "Non AC Bus")
    )

    val busCategoryOptions = listOf(
        RadioOption(id = "0_sleeper", title = "Sleeper"),
        RadioOption(id = "1_seater", title = "Seater")
    )

    // Scrollable Column for the form
    Column(
        modifier = Modifier.fillMaxSize()
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(16.dp) // Increased spacing
    ) {
        AppIconTopBar {
            onBackPressed()
        }
        Column(modifier = Modifier.padding(16.dp).align(Alignment.CenterHorizontally)) {
            Text("Bus Booking Request",
                modifier = Modifier.align(Alignment.CenterHorizontally),
                style = MaterialTheme.typography.headlineMedium)
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))

            // --- Text Inputs (remain the same) ---
            OutlinedTextField(
                value = name,
                onValueChange = { name = it },
                label = { Text("Name*") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true
            )
            OutlinedTextField(
                value = sourceLocation,
                onValueChange = { sourceLocation = it },
                label = { Text("Departure Location*") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true
            )
            OutlinedTextField(
                value = destinationLocation,
                onValueChange = { destinationLocation = it },
                label = { Text("Arrival Location*") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true
            )
            OutlinedTextField(
                value = date,
                onValueChange = { date = it },
                label = { Text("Date*") },
                placeholder = { Text("YYYY-MM-DD") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true
            )
            OutlinedTextField(
                value = days,
                onValueChange = { if (it.all { c -> c.isDigit() }) days = it },
                label = { Text("For how many days*") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
            )

            // --- Bus Size ---
            RadioGridSelection(
                label = "Bus Size*",
                options = busSizeOptions,
                selectedOptionId = selectedBusSize,
                onOptionSelected = { selectedBusSize = it },
                getIconForOption = { // Function to map option ID to an Icon
                    when (it.id) {
                        "0_minubus" -> Icons.Default.AirportShuttle // Smaller bus icon
                        "1_mini_coach" -> Icons.Default.DirectionsBus // Mid-size bus icon
                        "2_coach_bus" -> Icons.Default.DirectionsBusFilled // Larger bus icon
                        else -> Icons.Default.HelpOutline // Fallback icon
                    }
                }
            )

            // --- Bus Type ---
            RadioGridSelection(
                label = "Bus Type*",
                options = busTypeOptions,
                selectedOptionId = selectedBusType,
                onOptionSelected = { selectedBusType = it },
                getIconForOption = {
                    when (it.id) {
                        "0_ac" -> Icons.Default.AcUnit // AC icon
                        "1_nonac" -> Icons.Default.WbSunny // Sun icon for non-AC (example)
                        else -> Icons.Default.HelpOutline
                    }
                }
            )

            // --- Bus Category ---
            RadioGridSelection(
                label = "Bus Category*",
                options = busCategoryOptions,
                selectedOptionId = selectedBusCategory,
                onOptionSelected = { selectedBusCategory = it },
                getIconForOption = {
                    when (it.id) {
                        "0_sleeper" -> Icons.Default.Hotel // Bed/Hotel icon for sleeper
                        "1_seater" -> Icons.Default.EventSeat // Seat icon
                        else -> Icons.Default.HelpOutline
                    }
                }
            )
            OutlinedTextField(
                value = phoneNumber,
                onValueChange = { phoneNumber = it },
                label = { Text("Phone Number*") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone)
            )
            // --- Budget (remains the same) ---
            OutlinedTextField(
                value = budget,
                onValueChange = { newValue ->
                    if (newValue.isEmpty() || newValue.matches(Regex("^\\d*\\.?\\d*\$"))) {
                        budget = newValue
                    }
                },
                label = { Text("Budget (Optional)") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal)
            )


            Spacer(modifier = Modifier.height(16.dp))

            // --- Submit Button (remains the same) ---
            Button(
                onClick = {
                    // 1. Basic Validation (Optional but recommended)
                    if (name.isBlank() || sourceLocation.isBlank() || destinationLocation.isBlank() ||
                        date.isBlank() || days.isBlank() || selectedBusSize == null ||
                        selectedBusType == null || selectedBusCategory == null) {
                        openDialog = true
                        openDialogTitle = "Error"
                        openDialogText =  "Please fill all required fields (*)"
                        return@Button // Stop processing if validation fails
                    }


                    // 2. Find selected option titles (more user-friendly than IDs)
                    val busSizeTitle = busSizeOptions.find { it.id == selectedBusSize }?.title ?: "N/A"
                    val busTypeTitle = busTypeOptions.find { it.id == selectedBusType }?.title ?: "N/A"
                    val busCategoryTitle = busCategoryOptions.find { it.id == selectedBusCategory }?.title ?: "N/A"

                    // 3. Format the message string
                    val message = """
                    *New Bus Booking Request (https://neeta-1160d.web.app/)*

                    *Name:* $name
                    *Phone Number:* $phoneNumber
                    *Departure Location:* $sourceLocation
                    *Arrival Location:* $destinationLocation
                    *Date:* $date
                    *Duration:* $days day(s)
                    *Bus Size:* $busSizeTitle
                    *Bus Type:* $busTypeTitle
                    *Bus Category:* $busCategoryTitle
                    *Budget:* ${budget.ifBlank { "Not specified" }}
                """.trimIndent() // trimIndent removes leading whitespace

                    val parameters = HashMap<String,Any>()
                    parameters.put(name,message)
                    coroutineScope.launch {
                        firebaseDatabase.postFirebaseDatabase(
                            listOf("busBooking"), parameters, object : onCompletion<String> {
                                override fun onSuccess(t: String) {
                                    println("success")
                                }

                                override fun onError(e: Exception) {
                                    println("error")
                                }
                            })
                    }
                    val targetWhatsAppNumber = "919224683787"
                    // 5. Create the WhatsApp Intent URI
                    // Using wa.me link - more universal
                    val uri = "https://wa.me/$targetWhatsAppNumber?text=$message"
                    URIHandler.openUri(uri)

                },
                modifier = Modifier.align(Alignment.CenterHorizontally)
            ) {
                Text("Submit Request")
            }


            if (openDialog) {
                AlertDialog(
                    onDismissRequest = { openDialog = false },
                    title = { Text(text = openDialogTitle) },
                    text = { Text(openDialogText) },
                    confirmButton = {
                        Button(
                            onClick = { openDialog = false }) {
                            Text("Ok")
                        }
                    }
                )
            }
        }
    }
}

/**
 * Displays radio options in a horizontal grid using Cards.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun RadioGridSelection(
    label: String,
    options: List<RadioOption>,
    selectedOptionId: String?,
    onOptionSelected: (String) -> Unit,
    getIconForOption: (RadioOption) -> ImageVector, // Function to get icon
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier.fillMaxWidth()) {
        Text(label, style = MaterialTheme.typography.titleMedium)
        Spacer(modifier = Modifier.height(8.dp))

        // Use Row for horizontal arrangement. For more complex grids or wrapping,
        // consider FlowRow (androidx.compose.foundation.layout.FlowRow - experimental)
        // or LazyVerticalGrid. For 2-3 items, Row is often sufficient.
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(12.dp, Alignment.CenterHorizontally), // Space items and center if space allows
            verticalArrangement = Arrangement.spacedBy(12.dp, Alignment.CenterVertically)
        ) {
            options.forEach { option ->
                // Use weight if you want cards to expand equally.
                // Otherwise, rely on internal card padding/sizing.
                // Modifier.weight(1f)
                RadioCard(
                    option = option,
                    icon = getIconForOption(option),
                    isSelected = option.id == selectedOptionId,
                    onClick = { onOptionSelected(option.id) }
                )
            }
        }
        HorizontalDivider(modifier = Modifier.padding(top = 16.dp)) // Add divider after the group
    }
}

/**
 * A Card composable representing a single radio button option with an icon.
 */
@OptIn(ExperimentalMaterial3Api::class) // Needed for Card onClick
@Composable
fun RadioCard(
    option: RadioOption,
    icon: ImageVector,
    isSelected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val cardColors = CardDefaults.cardColors(
        containerColor = if (isSelected) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant,
        contentColor = if (isSelected) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurfaceVariant
    )
    val border = if (isSelected) BorderStroke(2.dp, MaterialTheme.colorScheme.primary) else null

    Card(
        onClick = onClick,
        colors = cardColors,
        border = border,
        elevation = CardDefaults.cardElevation(defaultElevation = if (isSelected) 4.dp else 1.dp)
    ) {
        Column(
            modifier = Modifier
                .padding(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Icon(
                imageVector = icon,
                contentDescription = option.title, // Accessibility
                modifier = Modifier.size(36.dp) // Adjust icon size
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = option.title,
                style = MaterialTheme.typography.bodyMedium,
                textAlign = TextAlign.Center
            )
        }
    }
}